import { players, type Player } from "../state/players";
import { upkeep } from "../state/features";
import type { Header } from "../replay/format";
import type { WorldState } from "../state/world";
import { bar, setBar, type Trend } from "../hud/TopBar";
import { clock, count, int, pct } from "./format";
import { oreRGB } from "../render/colour";

/** A count in a few characters (12.3k, 1.2M). */
function short(n: number): string {
  const a = Math.abs(n);
  const s = a >= 1e6 ? `${(a / 1e6).toFixed(1)}M` : a >= 1e4 ? `${Math.round(a / 1e3)}k` : a >= 1e3 ? `${(a / 1e3).toFixed(1)}k` : `${Math.round(a)}`;
  return n < 0 ? `−${s}` : s;
}

/** A change per minute, signed ("+1.2k/min", "−40/min"). */
function perMin(n: number): string {
  return `${n > 0 ? "+" : ""}${short(n)}/min`;
}

/** Seconds of history a rate is measured over. */
const WINDOW_S = 60;

interface Sample {
  t: number;
  alive: number;
  stock: number | null;
}

/** A station from the commander's state: its depot and freight (good id 1
 *  up, as the state's `goods` name them). */
interface StateStation {
  id: number;
  at: [number, number];
  freight: number[];
  /** Room for each good (shared), when the server says. */
  freight_capacity?: number;
}

/** The Goods panel's goods, in order: raw, made, then what's spent (the
 *  state's names; people aren't a good but are listed with them). */
const ALL_GOODS = ["Iron ore", "Copper ore", "Coal", "Oil", "Steel", "Copper", "Fuel", "Munitions", "Recruits", "People"];

/** A commander's resources on the bar: label, the good it counts (by name;
 *  rounds are depots' stock, people the population's pool), and in a line
 *  what makes and spends it. */
const RESOURCES: { label: string; good: string; chain: string }[] = [
  { label: "Steel", good: "Steel", chain: "Made at steelworks from iron ore and coal. Pays for building and for units." },
  { label: "Rounds", good: "Munitions", chain: "Made at munitions plants from steel. Trains carry them out to your units." },
  { label: "Recruits", good: "Recruits", chain: "Signed up at recruiting offices from your people. Barracks train them." },
  { label: "People", good: "", chain: "Your population: recruiting draws on it, and it grows back." },
  { label: "Credits", good: "", chain: "Struck at mints from copper. Pays in trade with no track needed, even across the storm wall." },
];

/** Works out the top bar's figures each frame (units and munitions per
 *  side, their trends and rates) and writes them to its store. */
export class Hud {
  private lastFrame = -2;
  /** The commander's credits (the state's ledger). */
  private credits = 0;
  /** Recent samples per side, for change per minute. */
  private history: Sample[][];
  private now: { alive: number; contact: number; dry: number; stock: number | null; dAlive: number | null; dStock: number | null }[];

  /** A commander's side (their resources on the bar), the latest state
   *  (stations, population) and each resource's recent samples. */
  private own: number | null = null;
  private stations: StateStation[] = [];
  private goods: string[] = [];
  private population: { pool: number; in_service: number; growth_per_hour_mille: number; ceiling?: number } | null = null;
  /** What the server says was made and used of each good (by name) per
   *  minute since its last report, in all and per station. */
  private economy = new Map<string, { stock: number; made: number; used: number }>();
  private stationEconomy = new Map<number, Map<string, { made: number; used: number }>>();
  private resHistory: { t: number; v: number }[][] = RESOURCES.map(() => []);
  private resNow: { v: number; rate: number | null; where: string[] }[] = RESOURCES.map(() => ({ v: 0, rate: null, where: [] }));

  constructor(
    private header: Header,
    menu: () => void,
  ) {
    this.history = header.factions.map(() => []);
    this.now = header.factions.map(() => ({ alive: 0, contact: 0, dry: 0, stock: null, dAlive: null, dStock: null }));
    setBar({
      title: header.title || "Replay",
      clock: "",
      sides: header.factions.map((f) => ({ name: f.name, color: f.color, units: "", rounds: "", unitsTrend: "" as Trend, roundsTrend: "" as Trend })),
      tips: header.factions.map((_, i) => () => this.tip(i)),
      own: null,
      resources: [],
      resourceTip: (i: number) => this.resourceTip(i),
      goods: [],
      goodsOpen: false,
      menu,
    });
    // Known from the hello already (watchers get no state).
    this.namePlayers(players());
  }

  /** A commander: their side's resources go on the bar. */
  setOwn(faction: number): void {
    this.own = faction;
    setBar("own", faction);
    setBar(
      "resources",
      RESOURCES.map((r) => ({ label: r.label, value: "", rate: "", trend: "" as Trend, income: "", expense: "" })),
    );
  }

  /** The Goods panel's rows: each good's stock across your stations (rounds:
   *  your depots'), made and used a minute, the net, and your room for it.
   *  Rounds' stock comes from this frame, the rest from the state. */
  private goodsRows(depots: { faction: number; stock: number; capacity: number }[]): void {
    if (this.own === null || !this.goods.length) return;
    const own = depots.filter((d) => d.faction === this.own);
    const room = this.stations.reduce((a, s) => a + (s.freight_capacity ?? 0), 0);
    const held = (g: string) => {
      const i = this.goods.indexOf(g) - 1;
      return this.stations.reduce((a, s) => a + (s.freight[i] ?? 0), 0);
    };
    // Ores in their atlas colour (copper the metal isn't an ore).
    const hue = (g: string) => {
      const ore = g === "Coal" || g === "Oil" ? g : g.endsWith(" ore") ? g.replace(/ ore$/, "") : "";
      return ore ? `rgb(${oreRGB(ore).map((v) => Math.round(v * 255)).join(",")})` : "";
    };
    setBar(
      "goods",
      ALL_GOODS.map((g) => {
        const e = this.economy.get(g);
        const people = g === "People";
        const stock = people ? (this.population?.pool ?? 0) : g === "Munitions" ? own.reduce((a, d) => a + d.stock, 0) : (e?.stock ?? held(g));
        const cap = people ? 0 : g === "Munitions" ? own.reduce((a, d) => a + d.capacity, 0) : room;
        const net = e ? e.made - e.used : null;
        return {
          name: g === "Munitions" ? "Rounds" : g,
          color: hue(g),
          stock: int(stock),
          room: cap ? ` / ${short(cap)}` : "",
          // Nothing made or used shows nothing, not "+0".
          made: e?.made ? `+${short(e.made)}` : "",
          used: e?.used ? `−${short(e.used)}` : "",
          net: !net ? "" : perMin(net).replace("/min", ""),
          trend: (net === null || net === 0 ? "" : net > 0 ? "up" : "down") as Trend,
        };
      }),
    );
  }

  /** The commander's state (every 5 s): their stations and population. */
  /** The top bar's sides named by their commanders (where signed in). */
  private named = new Map<number, string>();
  private namePlayers(list: Player[]): void {
    for (const p of list) {
      // Only what changed (the store's writes aren't free, and the list is
      // every player, every state).
      const was = `${p.name}
${p.avatar}`;
      if (this.named.get(p.side) === was) continue;
      this.named.set(p.side, was);
      if (p.side < this.header.factions.length) {
        setBar("sides", p.side, "name", p.name);
        setBar("sides", p.side, "avatar", p.avatar);
      }
    }
  }

  setState(state: Record<string, unknown>): void {
    // Sides by their commanders' names and avatars, where signed in.
    this.namePlayers((state.players as Player[] | undefined) ?? []);
    this.stations = (state.stations as StateStation[] | undefined) ?? [];
    this.goods = (state.goods as string[] | undefined) ?? [];
    this.population = (state.population as typeof this.population | undefined) ?? null;
    this.credits = (state.credits as number | undefined) ?? 0;
    type Rates = { good: number; made_per_min: number; used_per_min: number };
    const economy = state.economy as { goods?: (Rates & { stock: number })[]; stations?: { id: number; goods: Rates[] }[] } | undefined;
    this.economy.clear();
    this.stationEconomy.clear();
    for (const g of economy?.goods ?? []) this.economy.set(this.goods[g.good], { stock: g.stock, made: g.made_per_min, used: g.used_per_min });
    for (const st of economy?.stations ?? []) {
      this.stationEconomy.set(st.id, new Map(st.goods.map((g) => [this.goods[g.good], { made: g.made_per_min, used: g.used_per_min }])));
    }
  }

  private resourceTip(i: number): string {
    const r = RESOURCES[i];
    const n = this.resNow[i];
    const e = this.economy.get(r.good);
    const lines = [
      e
        ? `${int(n.v)} ${r.label.toLowerCase()}: ${int(e.made)} made, ${int(e.used)} used a minute`
        : `${int(n.v)} ${r.label.toLowerCase()}${n.rate === null ? "" : `, ${perMin(n.rate)}`}`,
    ];
    if (r.label === "People" && this.population) {
      const p = this.population;
      lines.push(`${int(p.in_service)} in service · grows ${(p.growth_per_hour_mille / 10).toFixed(1)}% an hour`);
      // Why the population can (or can't) grow: room on settled ground.
      if (typeof p.ceiling === "number") {
        lines.push(
          p.ceiling > 0
            ? `Room for ${int(p.ceiling)} people: from settled ground (your stations and depots, where no armed units are, and your units hold it).`
            : "No room for people yet: settle ground. Your stations and depots settle it where no armed units are, and your units hold it.",
        );
      }
    }
    // Where it is, when it's in more than one place.
    if (n.where.length > 1) lines.push(...n.where);
    const u = upkeep();
    if (r.good === "Steel" && u) {
      lines.push(`Upkeep: ${int(u.perMin)} a minute for your army (a tenth of each unit's steel an hour).${u.unpaid ? " Unpaid: your barracks have stopped until there's steel for it." : ""}`);
    }
    lines.push(r.chain);
    return lines.join("\n");
  }

  /** Each resource's stock now: rounds from this frame's depots; steel and
   *  recruits per station, from the last state (frames' station freight
   *  goes stale in a live world as stations are built), or this frame's
   *  before the first state; people from the state. */
  private resources(state: WorldState): { v: number; where: string[] }[] {
    const own = this.own!;
    const names = this.header.freight_goods ?? [];
    const out = RESOURCES.map(() => ({ v: 0, where: [] as string[] }));
    const at = (tile: number) => `${state.q(tile)}, ${state.r(tile)}`;
    const depots = state.depots.filter((d) => d.faction === own);
    out[1].v = depots.reduce((a, d) => a + d.stock, 0);
    if (depots.length > 1) out[1].where = depots.map((d) => `${int(d.stock)} at ${at(d.tile)}`);
    // Stations: the state's list, or (before the first state) this frame's.
    const ids = this.stations.length ? this.stations.map((s) => s.id) : [...state.freight.keys()].filter((d) => state.depots[d]?.faction === own);
    RESOURCES.forEach((r, i) => {
      if (!r.good || r.good === "Munitions") return;
      const frameSlot = names.indexOf(r.good);
      const stateSlot = this.goods.indexOf(r.good) - 1;
      for (const id of ids) {
        const st = this.stations.find((s) => s.id === id);
        const row = st ? null : state.freight.get(id);
        const n = st ? (st.freight[stateSlot] ?? 0) : row && frameSlot >= 0 ? row[frameSlot] : 0;
        out[i].v += n;
        const d = state.depots[id];
        const e = this.stationEconomy.get(id)?.get(r.good);
        const flow = e && (e.made || e.used) ? ` (+${int(e.made)} −${int(e.used)}/min)` : "";
        if (d) out[i].where.push(`${int(n)} at ${at(d.tile)}${flow}`);
      }
    });
    out[3].v = this.population?.pool ?? 0;
    out[4].v = Math.floor(this.credits);
    return out;
  }

  private tip(f: number): string {
    const n = this.now[f];
    const lines = [`${count(n.alive, "units")}${n.dAlive === null ? "" : `, ${perMin(n.dAlive)}`}`, `${int(n.contact)} in contact, ${pct(n.dry, n.contact)} out of ammo`];
    if (n.stock !== null) lines.push(`${int(n.stock)} rounds in depots${n.dStock === null ? "" : `, ${perMin(n.dStock)}`}`);
    return lines.join("\n");
  }

  /** `depots`: this frame's depots, for each side's munitions in stock
   *  (BAR's resource bar); `tick`: the battle clock. */
  update(frame: number, tick: number, depots: { faction: number; stock: number }[] = [], state?: WorldState): void {
    if (frame === this.lastFrame || frame < 0) return;
    this.lastFrame = frame;
    const t = tick / this.header.tick_rate;
    setBar("clock", document.body.classList.contains("live") ? clock(t) : "");
    if (this.own !== null && state) {
      this.updateResources(t, state);
      // The panel, while it's open (rounds change by the frame).
      if (bar.goodsOpen || !bar.goods.length) this.goodsRows(state.depots);
    }
    const s = this.header.summary;
    const stock = new Map<number, number>();
    // Fogged: only the viewer's own side's stock is known.
    const fog = this.header.fog;
    for (const d of depots) if (fog === undefined || d.faction === fog) stock.set(d.faction, (stock.get(d.faction) ?? 0) + d.stock);
    // Trend: rising or falling by more than half a percent a minute.
    const trend = (d: number | null, of: number): Trend => (d !== null && Math.abs(d) >= Math.max(1, of * 0.005) ? (d > 0 ? "up" : "down") : "");
    this.now.forEach((n, f) => {
      // Fogged, the server sends no summary rows (they'd tell of the enemy):
      // your own side is counted from the frame, others are unknown.
      const row = s.alive[frame];
      const alive = row ? (row[f] ?? 0) : state && f === this.own ? [...state.live].filter((u) => state.faction[u] === f).length : 0;
      const st = stock.has(f) ? stock.get(f)! : null;
      // History: a seek backwards starts it over.
      const h = this.history[f];
      if (h.length && t < h[h.length - 1].t) h.length = 0;
      h.push({ t, alive, stock: st });
      while (h.length > 2 && t - h[1].t >= WINDOW_S) h.shift();
      const old = h[0];
      const span = t - old.t;
      const rate = (a: number, b: number) => ((a - b) * 60) / span;
      n.alive = alive;
      n.contact = s.in_contact[frame]?.[f] ?? 0;
      n.dry = s.dry_in_contact[frame]?.[f] ?? 0;
      n.stock = st;
      n.dAlive = span >= 10 ? rate(alive, old.alive) : null;
      n.dStock = span >= 10 && st !== null && old.stock !== null ? rate(st, old.stock) : null;
      setBar("sides", f, {
        units: int(alive),
        rounds: st !== null ? `${short(st)} rounds` : "",
        unitsTrend: trend(n.dAlive, alive),
        roundsTrend: trend(n.dStock, st ?? 0),
      });
    });
  }

  /** A commander's resources on the bar: stock, and change over the last
   *  minute (state-fed ones step every few seconds, so a minute smooths them). */
  private updateResources(t: number, state: WorldState): void {
    const now = this.resources(state);
    now.forEach((r, i) => {
      const h = this.resHistory[i];
      if (h.length && t < h[h.length - 1].t) h.length = 0;
      h.push({ t, v: r.v });
      while (h.length > 2 && t - h[1].t >= WINDOW_S) h.shift();
      const span = t - h[0].t;
      const rate = span >= 10 ? ((r.v - h[0].v) * 60) / span : null;
      this.resNow[i] = { v: r.v, rate, where: r.where };
      const trend: Trend = rate !== null && Math.abs(rate) >= Math.max(1, r.v * 0.005) ? (rate > 0 ? "up" : "down") : "";
      // The server's made and used, when it reports them (not people).
      const e = this.economy.get(RESOURCES[i].good);
      const flow = e && (e.made > 0 || e.used > 0);
      setBar("resources", i, {
        value: short(r.v),
        rate: trend ? perMin(rate!) : "",
        trend,
        // A side that's zero says nothing (not "−0").
        income: flow && e.made > 0 ? `+${short(e.made)}` : "",
        expense: flow && e.used > 0 ? `−${short(e.used)}` : "",
        // The army's steel upkeep (v31), beside what's made and used.
        upkeep: RESOURCES[i].good === "Steel" && upkeep() && upkeep()!.perMin >= 0.05 ? `−${upkeep()!.perMin < 10 ? upkeep()!.perMin.toFixed(1) : short(upkeep()!.perMin)}` : "",
        unpaid: RESOURCES[i].good === "Steel" && !!upkeep()?.unpaid,
      });
    });
  }
}
